import {
    type PropsWithChildren,
    createContext,
    useContext,
    useEffect,
    useState,
} from 'react';

import { useAuthContext } from '@myphx-engineering/auth/client';

import { Loader } from '../components/common/Loader';
import lillyConfig from '../configuration/lilly-direct';
import lillyContentConfig from '../configuration/lilly-direct.content';
import type { ProgramContentData } from '../configuration/shared';
import {
    type MedicationConfigOverride,
    isConfigApiAuthError,
    useConfigApi,
} from '../hooks/use-config-api';
import type { DrugProgram } from '../types/drug-config';

export type DrugProgramCtx = {
    drugProgram: DrugProgram | null;
    medicationConfigOverride: MedicationConfigOverride | null;
    programContentOverride: ProgramContentData | null;
};

export const DrugProgramContext = createContext<DrugProgramCtx>({
    drugProgram: null,
    medicationConfigOverride: null,
    programContentOverride: null,
});

const authClientId = import.meta.env.VITE_AUTH_CLIENT_ID;

export const DrugProgramProvider = ({ children }: PropsWithChildren) => {
    const [config, setConfig] = useState<{
        drugProgram: DrugProgram | null;
        medicationConfigOverride: MedicationConfigOverride | null;
        programContentOverride: ProgramContentData | null;
    }>({
        drugProgram: null,
        medicationConfigOverride: null,
        programContentOverride: null,
    });
    const [loadState, setLoadState] = useState<
        'uninitialized' | 'loading' | 'loaded' | 'error' | 'unauthorized'
    >('uninitialized');
    const configApi = useConfigApi();
    const { isAuthenticated, signIn } = useAuthContext();

    useEffect(() => {
        async function initializeConfig() {
            setLoadState('loading');

            try {
                const result1 = configApi.getProgramByAppId(authClientId);
                const result2 = Promise.resolve(lillyConfig);
                const result3 = Promise.resolve(lillyContentConfig);

                const [
                    drugProgram,
                    medicationConfigOverride,
                    programContentOverride,
                ] = await Promise.all([result1, result2, result3]);

                setConfig({
                    drugProgram,
                    medicationConfigOverride,
                    programContentOverride,
                });
                setLoadState('loaded');
            } catch (error) {
                // oxlint-disable-next-line no-console
                console.error('Error loading config:', error);
                if (isConfigApiAuthError(error)) {
                    setLoadState('unauthorized');
                    signIn(
                        `${window.location.pathname}${window.location.search}`
                    );
                    return;
                }
                setLoadState('error');
            }
        }

        if (isAuthenticated) {
            if (loadState === 'uninitialized') {
                void initializeConfig();
            }
        }
    }, [isAuthenticated, configApi, loadState, signIn]);

    if (
        loadState === 'error' ||
        (loadState === 'loaded' && !config.drugProgram)
    ) {
        throw new Error('Error loading program configuration');
    }

    // 'unauthorized' means signIn() has already been called and the redirect is in
    // flight. Keep holding the Loader rather than rendering children against a null
    // config, which the selector hooks would (correctly) throw on.
    if (
        isAuthenticated &&
        (loadState === 'loading' ||
            loadState === 'uninitialized' ||
            loadState === 'unauthorized')
    ) {
        return <Loader />;
    }

    return (
        <DrugProgramContext.Provider value={config}>
            {children}
        </DrugProgramContext.Provider>
    );
};

export const useDrugProgram = (): DrugProgram => {
    const ctx = useContext(DrugProgramContext);

    if (ctx.drugProgram === null) {
        throw new Error(
            'useDrugProgram() cannot be called outside a DrugProgramProvider'
        );
    }

    return ctx.drugProgram;
};

export const useMedicationConfigOverride = (): MedicationConfigOverride => {
    const ctx = useContext(DrugProgramContext);

    if (ctx.medicationConfigOverride === null) {
        throw new Error(
            'useMedicationConfigOverride() cannot be called outside a DrugProgramProvider'
        );
    }

    return ctx.medicationConfigOverride;
};

export const useProgramContentOverride = (): ProgramContentData => {
    const ctx = useContext(DrugProgramContext);

    if (ctx.programContentOverride === null) {
        throw new Error(
            'useProgramContentOverride() cannot be called outside a DrugProgramProvider'
        );
    }

    return ctx.programContentOverride;
};
